import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, catchError, forkJoin, map, throwError } from "rxjs";

import { BudgetItemRead, Unit } from "../../../../shared/general/general.models";
import { GeneralService } from "../../../../shared/general/general.service";
import { AnalyticsService } from "../../../../shared/services/analytics/analytics.service";
import { HttpOptions } from "../../../../shared/services/base/base.models";
import { LoadingService } from "../../../../shared/services/loading/loading.service";
import { BudgetAuthErrorCode, BudgetAuthErrorMessage } from "../../budget-auth/budget-auth.data";
import { AnalyticsEvents } from "@app/shared/services/analytics/analytics.models";

const sendAnalyticsEvents = (err: any) => {
  AnalyticsService.logEvent({
    name: AnalyticsEvents.EXCEPTION,
    params: {
      description: `${err.code}:orcamento-indisponivel`,
      fatal: false,
    },
  });
};

@Injectable({
  providedIn: "root",
})
export class BudgetService {
  private readonly router = inject(Router);

  private readonly generalService = inject(GeneralService<{ data: BudgetItemRead }>);

  private readonly loadingService = inject(LoadingService);

  private currentBudget$: BehaviorSubject<BudgetItemRead | undefined> = new BehaviorSubject<
    BudgetItemRead | undefined
  >(undefined);

  private quotationId = "";

  getCurrentBudgetValue() {
    return this.currentBudget$.getValue();
  }

  setCurrentBudget(budget: BudgetItemRead | undefined) {
    this.currentBudget$.next(budget);
  }

  getById(id: string, hash = "") {
    this.quotationId = id;

    const options: HttpOptions = {
      headers: {
        "vs-auth-hash": hash,
        "vs-deprecated-account-balance": "1",
      },
    };

    const mobile$ = this.generalService.getById(`quotations/${id}`!, options).pipe(
      map((res) => {
        const isAllSpecialtiesAvailable = res.data.units.map((unit: Unit) =>
          unit.items.every((item) => item.is_available),
        );

        res.data.isAllSpecialtiesAvailable = isAllSpecialtiesAvailable.every(
          (isAvailable: boolean) => isAvailable,
        );

        return res;
      }),
    );

    const desktop$ = this.generalService.getById(`quotations/${id}/desktop`!, options);

    return forkJoin([mobile$, desktop$]).pipe(
      catchError((err) => {
        sendAnalyticsEvents(err);

        if (err.code === BudgetAuthErrorCode.UNAUTHORIZED) {
          this.stopLoading();
          return throwError(() => ({
            ...err,
            message: BudgetAuthErrorMessage.UNAUTHORIZED,
          }));
        }

        this.router
          .navigate(["/erro"], {
            queryParams: { ...err, d: new Date().getTime(), step: "cliente-vivo:lp-orcamento" },
          })
          .then(this.stopLoading.bind(this));

        return throwError(() => err);
      }),
    );
  }

  getQuotationId() {
    return this.quotationId;
  }

  private stopLoading() {
    this.loadingService.dismissAll();
  }
}
